import React, { useState, useEffect, useRef } from 'react'
import { Form } from 'react-bootstrap'
import { Formik } from 'formik'
import classNames from 'classnames'
import LinkableTextEditor from './LinkableTextEditor'
import { useErrorService, useNotificationService } from '@abroad/components'
import Imgix from 'react-imgix'
import API from '../../utils/API'
import axios from 'axios'
import {
  ExperienceCheckbox,
  ExperienceInputField,
  ExperienceSelect,
} from '../form'
import Select from 'react-select'
import DeleteOptionModal from './DeleteOptionModal'
import { parseLinksFromHtml as parseLinksFromHtmlUtil } from './utils/linkUtils'
import ImageCropModal from '../common/ImageCropModal'
import ExperienceButton from './ExperienceButton'

const QUESTION_TYPES = [
  { value: 'text', label: 'Text' },
  { value: 'email', label: 'Email' },
  { value: 'phone', label: 'Phone Number' },
  { value: 'country', label: 'Country Code' },
  { value: 'singleSelectionDropdown', label: 'Single Selection Dropdown' },
  { value: 'multipleSelectionDropdown', label: 'Multiple Selection Dropdown' },
  { value: 'files', label: 'File Upload' },
  { value: 'date', label: 'Date' },
  { value: 'object', label: 'Address' },
  { value: 'scale', label: 'Scale' },
]

const FILE_CATEGORIES = [
  { value: 'passport', label: 'Passport' },
  { value: 'headshot', label: 'Headshot' },
  { value: 'insurance', label: 'Insurance' },
  { value: 'ticket', label: 'Ticket' },
]

// Function to reconstruct addressFields from subQuestions
const reconstructAddressFields = (question) => {
  // If addressFields is provided in the response, use it
  if (question.addressFields) {
    return question.addressFields
  }

  // Otherwise, reconstruct addressFields from subQuestions
  if (question.answerType === 'object' && question.subQuestions) {
    const addressFields = {
      address: false,
      address2: false,
      city: false,
      state: false,
      zip: false,
      country: false,
    }

    // Map subQuestion names back to addressFields keys
    question.subQuestions.forEach((subQuestion) => {
      const subQuestionName = subQuestion.subQuestion
      if (subQuestionName === 'Address') addressFields.address = true
      else if (subQuestionName === 'Address2') addressFields.address2 = true
      else if (subQuestionName === 'City') addressFields.city = true
      else if (subQuestionName === 'State') addressFields.state = true
      else if (subQuestionName === 'Zip') addressFields.zip = true
      else if (subQuestionName === 'Country') addressFields.country = true
    })

    return addressFields
  }

  // Default fallback
  return {
    address: true,
    address2: true,
    city: true,
    state: true,
    zip: true,
    country: true,
  }
}

const QuestionEditor = ({
  question,
  actualIdx,
  displayIdx,
  formState,
  validationErrors,
  setValidationErrors,
  onDelete,
  onMove,
  onUpdate,
  questId,
  imageRefs,
  allQuestions,
  allQuestionsForCondition,
  allQuestionsForParent,
  canMoveUp,
  canMoveDown,
  totalQuestions,
  hideActionButtons = false,
  saveMode = 'append',
  insertAfterIndex = null,
  existingQuestions = [],
  onSave,
  onCancel,
  isSaving = false,
  onDeleteModalStateChange,
  onLinkModalStateChange,
  onImageUploadStateChange,
}) => {
  const [isSavingQuestion, setIsSavingQuestion] = useState(false)
  const Error = useErrorService()
  const Notification = useNotificationService()
  const [removingImageIdx, setRemovingImageIdx] = useState(null)
  const [showLinkModal, setShowLinkModal] = useState(false)
  const [showImageCropModal, setShowImageCropModal] = useState(false)
  const [selectedImageFile, setSelectedImageFile] = useState(null)
  const [isDraggingImage, setIsDraggingImage] = useState(false)
  const inputResetRef = useRef(null)
  const localImageRef = useRef(null)

  // The link styling, selection tracking and auto-resize are managed inside LinkableTextEditor now

  // Modal state for option deletion warning
  const [deleteOptionModal, setDeleteOptionModal] = useState({
    show: false,
    optionText: '',
    dependentQuestions: [],
  })

  // Notify parent component when delete modal state changes
  useEffect(() => {
    if (onDeleteModalStateChange) {
      onDeleteModalStateChange(deleteOptionModal.show)
    }
  }, [deleteOptionModal.show, onDeleteModalStateChange])

  // Notify parent component when link modal state changes
  useEffect(() => {
    if (onLinkModalStateChange) {
      onLinkModalStateChange(showLinkModal)
    }
  }, [showLinkModal, onLinkModalStateChange])

  // Function to handle option deletion with dependency checking
  const handleOptionDelete = (
    optionText,
    optIdx,
    currentQuestion,
    allQuestionsForCondition,
  ) => {
    // Get the option ID for the current option text
    const currentOption = currentQuestion.originalOptions?.[optIdx]
    const optionId = currentOption?.id || currentOption?._id

    if (!allQuestionsForCondition || allQuestionsForCondition.length === 0) {
      return { canDelete: true, dependentQuestions: [] }
    }

    const dependentQuestions = allQuestionsForCondition.filter((q) => {
      // Skip the current question being edited
      if (q.id === currentQuestion.id || q._id === currentQuestion.id) {
        return false
      }

      // Check if this question is dependent on the current question
      if (
        q.questionType !== 'dependent' ||
        q.dependentQuestionId !== (currentQuestion.id || currentQuestion._id)
      ) {
        return false
      }

      // Check if the condition matches the option we're trying to delete
      if (!q.condition || q.condition.length === 0) {
        return false
      }

      // Only check if we have a valid optionId
      if (!optionId) {
        return false
      }

      // For single selection, check if the condition matches the option ID
      if (currentQuestion.answerType === 'singleSelectionDropdown') {
        return q.condition[0] === optionId
      }

      // For multiple selection, check if the condition array contains the option ID
      if (currentQuestion.answerType === 'multipleSelectionDropdown') {
        return q.condition.includes(optionId)
      }

      return false
    })

    const dependentQuestionsWithNumbers = dependentQuestions.map(
      (q, index) => ({
        ...q,
        displayNumber:
          allQuestionsForCondition.findIndex(
            (q2) => q2.id === q.id || q2._id === q.id,
          ) + 1,
      }),
    )

    return {
      canDelete: dependentQuestionsWithNumbers.length === 0,
      dependentQuestions: dependentQuestionsWithNumbers,
    }
  }

  // Get available parent questions (dropdowns that have at least one filled option)
  const availableParentQuestions = allQuestions.filter((q) => {
    const hasFilledOption = (q.originalOptions || []).some((opt) => {
      const text = (opt?.option || opt?.text || '').trim()
      return text.length > 0
    })
    return (
      !q.isDeleted &&
      q.questionType !== 'dependent' &&
      q.text &&
      q.text.trim() &&
      (q.answerType === 'singleSelectionDropdown' ||
        q.answerType === 'multipleSelectionDropdown') &&
      hasFilledOption
    )
  })

  // Helper function to get option value by ID
  const getOptionValueById = (parentQuestion, optionId) => {
    if (!parentQuestion?.originalOptions) return null

    const optionsToCheck = parentQuestion.originalOptions || []

    const option = optionsToCheck.find((opt) => {
      const optId =
        typeof opt === 'object' && opt !== null ? opt.id || opt._id : null
      return optId === optionId
    })

    if (typeof option === 'object' && option !== null) {
      return option.option
    }
    return option
  }

  // Helper function to convert condition values to IDs
  const convertConditionToIds = (condition, parentQuestion) => {
    if (
      !condition ||
      !Array.isArray(condition) ||
      !parentQuestion?.originalOptions
    ) {
      return condition
    }

    return condition.map((conditionValue) => {
      // If conditionValue is already an ID (not a string that matches any option value), return it as is
      const optionsToCheck = parentQuestion.originalOptions || []
      const matchingOption = optionsToCheck.find((opt) => {
        const optValue =
          typeof opt === 'object' && opt !== null ? opt.option : opt
        return optValue === conditionValue
      })

      if (
        matchingOption &&
        typeof matchingOption === 'object' &&
        matchingOption !== null
      ) {
        return matchingOption.id || matchingOption._id || conditionValue
      }

      return conditionValue
    })
  }

  // Helper function to get parent questions with disabled state for those where all options are used
  const getParentQuestionsWithDisabledState = (currentQuestion) => {
    return availableParentQuestions.map((q) => {
      let isDisabled = false
      let disabledReason = ''

      // Check if all options are already used by other dependent questions
      if (q.originalOptions && q.originalOptions.length > 0) {
        const usedOptions = allQuestionsForCondition
          .filter((dependentQ) => {
            // Skip the current question being edited
            // For new questions, currentQuestion might not have an id yet
            const currentQuestionId =
              currentQuestion?.id || currentQuestion?._id
            const dependentQuestionId = dependentQ.id || dependentQ._id

            if (
              currentQuestionId &&
              dependentQuestionId === currentQuestionId
            ) {
              return false
            }

            return (
              dependentQ.questionType === 'dependent' &&
              dependentQ.dependentQuestionId === (q.id || q._id) &&
              dependentQ.condition &&
              dependentQ.condition.length > 0
            )
          })
          .map((dependentQ) => {
            // Get the condition ID directly
            const conditionId = dependentQ.condition[0]
            return conditionId
          })
          .filter(Boolean)

        // For single selection dropdowns, check if all options are used
        if (q.answerType === 'singleSelectionDropdown') {
          const allOptionIds = q.originalOptions
            .map((opt) => {
              return typeof opt === 'object' && opt !== null
                ? opt.id || opt._id
                : null
            })
            .filter(Boolean)

          const allOptionsUsed = allOptionIds.every((optionId) =>
            usedOptions.includes(optionId),
          )

          // If all options are used, mark as disabled
          // UNLESS the current question already has this parent question selected
          if (allOptionsUsed) {
            const currentQuestionParentId = currentQuestion?.dependentQuestionId
            const parentQuestionId = q.id || q._id

            // If the current question doesn't have this parent selected, disable it
            if (currentQuestionParentId !== parentQuestionId) {
              isDisabled = true
              disabledReason = ' (All options used)'
            }
          }
        }

        // For multiple selection dropdowns, we don't disable them as they can have multiple conditions
      }

      return {
        ...q,
        isDisabled,
        disabledReason,
        displayText: q.text + (isDisabled ? disabledReason : ''),
      }
    })
  }

  const handleFileClick = (index) => {
    const fileInputRef = imageRefs?.current?.[index] || localImageRef
    if (fileInputRef?.current) {
      fileInputRef.current.value = ''
      fileInputRef.current.click()
    }
  }

  const validateImageFile = (imageFile) => {
    const allowedFormats = ['JPG', 'JPEG', 'PNG']
    const fileExtension = imageFile?.name?.split('.').pop()?.toUpperCase()
    if (!allowedFormats.includes(fileExtension)) {
      Notification.showNotification(
        'Filetype must be one of [JPG, JPEG, PNG]',
        'danger',
      )
      return false
    }

    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png']
    const allowedExtensions = ['.jpg', '.jpeg', '.png']
    const fileType = imageFile.type.toLowerCase()
    const fileName = imageFile.name.toLowerCase()
    const hasValidExtension = allowedExtensions.some((ext) =>
      fileName.endsWith(ext),
    )
    if (!allowedTypes.includes(fileType) || !hasValidExtension) {
      Notification.showNotification(
        'Only JPG, JPEG, or PNG images are allowed.',
        'danger',
      )
      return false
    }
    return true
  }

  const resetImageInput = () => {
    if (typeof inputResetRef.current === 'function') {
      inputResetRef.current()
    }
    inputResetRef.current = null
  }

  const openCropModal = (imageFile, resetInput) => {
    if (!imageFile) return
    if (!validateImageFile(imageFile)) {
      if (resetInput) resetInput()
      return
    }
    inputResetRef.current = resetInput || null
    setSelectedImageFile(imageFile)
    setShowImageCropModal(true)
  }

  const handleCropModalClose = () => {
    setShowImageCropModal(false)
    setSelectedImageFile(null)
    setIsDraggingImage(false)
    resetImageInput()
  }

  const handleDragEnter = (e) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDraggingImage(true)
  }

  const handleDragOver = (e) => {
    e.preventDefault()
    e.stopPropagation()
    if (!isDraggingImage) setIsDraggingImage(true)
  }

  const handleDragLeave = (e) => {
    e.preventDefault()
    e.stopPropagation()
    if (!e.currentTarget.contains(e.relatedTarget)) {
      setIsDraggingImage(false)
    }
  }

  const handleDrop = (e) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDraggingImage(false)
    const files = e.dataTransfer?.files
    if (files && files.length > 0) {
      openCropModal(files[0])
      e.dataTransfer.clearData()
    }
  }

  const uploadQuestionImage = async (imageFile) => {
    try {
      const contentType = imageFile.name
        .substr(imageFile.name.lastIndexOf('.') + 1)
        .toUpperCase()

      const { data } = await API.AdminAPI.quests.getTypeformImage(
        questId,
        contentType,
      )
      if (data) {
        await axios.put(data.url.url, imageFile, {
          headers: {
            'Content-Type': data.url.contentType,
          },
        })
        return data.fileName
      } else {
        throw new Error('Image upload failed')
      }
    } catch (e) {
      throw e
    }
  }

  const handleRemoveQuestionImage = async (idx, question, setFieldValue) => {
    try {
      setRemovingImageIdx(idx)
      setFieldValue(`questions[0].isUploading`, true)
      if (onImageUploadStateChange) onImageUploadStateChange(true)
      await API.AdminAPI.quests.removeQuestionImage(
        questId,
        formState.customFormId,
        question.id,
      )
      setFieldValue(`questions[0].imageFileName`, '')
      // Notification.showNotification('Image removed successfully!', 'success')
    } catch (e) {
      Error.showError(e)
    } finally {
      setFieldValue(`questions[0].isUploading`, false)
      if (onImageUploadStateChange) onImageUploadStateChange(false)
      setRemovingImageIdx(null)
    }
  }

  const clearValidationError = (questionIdx, optionIdx, fieldName) => {
    if (!validationErrors[questionIdx]) {
      return
    }
    const updatedErrors = { ...validationErrors }
    const questionError = { ...updatedErrors[questionIdx] }

    if (fieldName) {
      delete questionError[fieldName]
    } else if (
      typeof optionIdx === 'number' &&
      Array.isArray(questionError.optionIndexes)
    ) {
      questionError.optionIndexes = questionError.optionIndexes.filter(
        (idx) => idx !== optionIdx,
      )
      if (questionError.optionIndexes.length === 0) {
        delete questionError.optionIndexes
        delete questionError.options
      }
    }

    if (Object.keys(questionError).length === 0) {
      delete updatedErrors[questionIdx]
    } else {
      updatedErrors[questionIdx] = questionError
    }

    setValidationErrors(updatedErrors)
  }

  const handleFormSubmit = async (values, { resetForm }) => {
    try {
      // Validate questions before submission
      const nextValidationErrors = {}

      values.questions.forEach((question) => {
        const questionError = {}
        const hasText = question.text && question.text.trim().length > 0

        if (!hasText) {
          questionError.text = 'Enter a question'
        }

        if (
          question.answerType === 'files' &&
          (!question.connectedTo || question.connectedTo.trim() === '')
        ) {
          questionError.connectedTo =
            'File category is required for file upload questions'
        }

        if (
          question.answerType === 'singleSelectionDropdown' ||
          question.answerType === 'multipleSelectionDropdown'
        ) {
          const blankOptionIndexes = []
          ;(question.originalOptions || []).forEach((opt, optIdx) => {
            const optionText = (opt?.option || opt?.text || '').trim()
            if (!optionText) {
              blankOptionIndexes.push(optIdx)
            }
          })
          const filledCount =
            (question.originalOptions || []).length - blankOptionIndexes.length
          if (filledCount === 0) {
            questionError.options = 'Add at least one option'
            questionError.optionIndexes =
              blankOptionIndexes.length > 0 ? blankOptionIndexes : [0]
          }
        }

        if (question.questionType === 'dependent') {
          if (!question.dependentQuestionId) {
            questionError.dependentQuestionId = 'Choose a parent question'
          } else {
            const parentQuestion = availableParentQuestions.find(
              (q) => (q.id || q._id) === question.dependentQuestionId,
            )
            if (!parentQuestion) {
              questionError.dependentQuestionId =
                'Parent must be a dropdown with at least one option'
            }
          }

          const hasCondition =
            Array.isArray(question.condition) &&
            question.condition.some(
              (c) => c !== null && c !== undefined && String(c).trim() !== '',
            )
          if (!hasCondition) {
            questionError.condition = 'Choose a condition'
          }
        }

        if (Object.keys(questionError).length > 0) {
          // QuestionEditor always edits questions[0]; map errors to list index.
          nextValidationErrors[actualIdx] = {
            ...nextValidationErrors[actualIdx],
            ...questionError,
          }
        }
      })

      // If there are validation errors, show them and stop submission
      if (Object.keys(nextValidationErrors).length > 0) {
        setValidationErrors(nextValidationErrors)
        return
      }

      setValidationErrors({})
      setIsSavingQuestion(true)

      let questionsToPersist = values.questions

      if (
        saveMode === 'insertBelow' &&
        Array.isArray(existingQuestions) &&
        typeof insertAfterIndex === 'number'
      ) {
        const questionsWithoutDeleted = existingQuestions.filter(
          (q) => !q.isDeleted,
        )
        const insertionIndex = Math.min(
          insertAfterIndex + 1,
          questionsWithoutDeleted.length,
        )
        const nextQuestions = [...questionsWithoutDeleted]
        nextQuestions.splice(insertionIndex, 0, values.questions[0])
        questionsToPersist = nextQuestions
      }

      // Filter out empty questions
      const questionsForPayload = questionsToPersist.filter((q) => {
        const hasText = q.text && q.text.trim().length > 0
        return hasText
      })

      const payload = {
        ...(formState.customFormId && {
          customFormId: formState.customFormId,
        }),
        questions: questionsForPayload.map((q) => {
          // Process links array using stored positions to HTML in question text
          let processedQuestionText = q.text || ''
          if (Array.isArray(q.links) && q.links.length > 0) {
            const linksWithPos = q.links.filter(
              (l) =>
                typeof l.startIndex === 'number' &&
                typeof l.length === 'number',
            )
            const linksWithoutPos = q.links.filter(
              (l) =>
                !(
                  typeof l.startIndex === 'number' &&
                  typeof l.length === 'number'
                ),
            )
            // Apply positioned links first
            if (linksWithPos.length > 0) {
              const sorted = [...linksWithPos].sort(
                (a, b) => (a.startIndex || 0) - (b.startIndex || 0),
              )
              let html = ''
              let cursor = 0
              sorted.forEach((l) => {
                const start = Math.max(
                  0,
                  Math.min(processedQuestionText.length, l.startIndex),
                )
                const end = Math.max(
                  start,
                  Math.min(processedQuestionText.length, start + l.length),
                )
                html += processedQuestionText.slice(cursor, start)
                const linkText =
                  processedQuestionText.slice(start, end) || l.text || ''
                if (l.url && linkText) {
                  html += `<a href="${l.url}" target="_blank" rel="noreferrer">${linkText}</a>`
                } else {
                  html += linkText
                }
                cursor = end
              })
              html += processedQuestionText.slice(cursor)
              processedQuestionText = html
            }
            // Then apply legacy links by simple replacement
            if (linksWithoutPos.length > 0) {
              linksWithoutPos.forEach((link) => {
                if (!link.text || !link.url) return
                const linkHtml = `<a href="${link.url}" target="_blank" rel="noreferrer">${link.text}</a>`
                processedQuestionText = processedQuestionText.replace(
                  link.text,
                  linkHtml,
                )
              })
            }
          }

          const questionPayload = {
            id: q._id || q.id,
            question: processedQuestionText,
            ...(q.answerType && { answerType: q.answerType }),
            ...(q.questionType && { questionType: q.questionType }),
            ...(q.questionType === 'dependent' &&
              q.dependentQuestionId && {
                dependentQuestionId: q.dependentQuestionId,
              }),
            ...(q.questionType === 'dependent' &&
              q.condition &&
              q.condition.length > 0 && {
                condition: (() => {
                  // Find the parent question to convert condition values to IDs
                  const parentQuestion = availableParentQuestions.find(
                    (parent) =>
                      (parent.id || parent._id) === q.dependentQuestionId,
                  )
                  return convertConditionToIds(q.condition, parentQuestion)
                })(),
              }),
            ...(q.imageFileName &&
            q.imageFileName.trim &&
            q.imageFileName.trim() !== ''
              ? { questionImageName: q.imageFileName }
              : {}),
            ...(q.position && q.position.trim && q.position.trim() !== ''
              ? { position: q.position }
              : {}),
            ...(q.answerType === 'files' &&
            q.connectedTo &&
            q.connectedTo.trim &&
            q.connectedTo.trim() !== ''
              ? { connectedTo: q.connectedTo }
              : {}),
            ...(q.columnNameforReport &&
            q.columnNameforReport.trim &&
            q.columnNameforReport.trim() !== ''
              ? { columnNameforReport: q.columnNameforReport }
              : {}),
            ...(q.isRequired ? { isRequired: true } : {}),
            isDeleted: false,
          }

          if (q.description) {
            // Render description with links similar to question text
            let processedDescription = q.description || ''
            if (
              Array.isArray(q.descriptionLinks) &&
              q.descriptionLinks.length > 0
            ) {
              const linksWithPos = q.descriptionLinks.filter(
                (l) =>
                  typeof l.startIndex === 'number' &&
                  typeof l.length === 'number',
              )
              const linksWithoutPos = q.descriptionLinks.filter(
                (l) =>
                  !(
                    typeof l.startIndex === 'number' &&
                    typeof l.length === 'number'
                  ),
              )
              if (linksWithPos.length > 0) {
                const sorted = [...linksWithPos].sort(
                  (a, b) => (a.startIndex || 0) - (b.startIndex || 0),
                )
                let html = ''
                let cursor = 0
                sorted.forEach((l) => {
                  const start = Math.max(
                    0,
                    Math.min(processedDescription.length, l.startIndex),
                  )
                  const end = Math.max(
                    start,
                    Math.min(processedDescription.length, start + l.length),
                  )
                  html += processedDescription.slice(cursor, start)
                  const linkText =
                    processedDescription.slice(start, end) || l.text || ''
                  if (l.url && linkText) {
                    html += `<a href="${l.url}" target="_blank" rel="noreferrer">${linkText}</a>`
                  } else {
                    html += linkText
                  }
                  cursor = end
                })
                html += processedDescription.slice(cursor)
                processedDescription = html
              }
              if (linksWithoutPos.length > 0) {
                linksWithoutPos.forEach((link) => {
                  if (!link.text || !link.url) return
                  const linkHtml = `<a href="${link.url}" target="_blank" rel="noreferrer">${link.text}</a>`
                  processedDescription = processedDescription.replace(
                    link.text,
                    linkHtml,
                  )
                })
              }
            }
            if (processedDescription && processedDescription.trim()) {
              questionPayload.description = processedDescription.trim()
            }
          }

          if (
            q.answerType === 'singleSelectionDropdown' ||
            q.answerType === 'multipleSelectionDropdown'
          ) {
            // Filter out empty options and map to proper structure
            questionPayload.options = (q.originalOptions || [])
              .filter((option) => {
                const optionText = option.option || option.text || ''
                return optionText.trim().length > 0
              })
              .map((option) => {
                const optionObj = {
                  option: option.option || option.text || '',
                }

                // Include id if it exists, or convert _id to id if _id exists
                if (option.id) {
                  optionObj.id = option.id
                } else if (option._id) {
                  optionObj.id = option._id
                }

                return optionObj
              })
          }

          if (q.answerType === 'object') {
            const originalSubQuestions = q.subQuestions || []
            const enabledFields = Object.entries(q.addressFields || {})
              .filter(([_, enabled]) => enabled)
              .map(([field, _]) => field)

            questionPayload.subQuestions = enabledFields.map((field) => {
              const subQuestionName =
                field.charAt(0).toUpperCase() + field.slice(1)

              const existingSubQuestion = originalSubQuestions.find(
                (sub) => sub.subQuestion === subQuestionName,
              )

              return {
                id: existingSubQuestion?._id || existingSubQuestion?.id,
                subQuestion: subQuestionName,
              }
            })
          }

          if (q.answerType === 'scale') {
            const scaleOptions = {}
            const maxLimit = q.scaleOptions?.maxScaleLimit
            if (
              typeof maxLimit === 'number' &&
              maxLimit >= 5 &&
              maxLimit <= 10
            ) {
              scaleOptions.maxScaleLimit = maxLimit
            }
            const low = q.scaleOptions?.lowSubtitle?.trim()
            if (low) scaleOptions.lowSubtitle = low
            const high = q.scaleOptions?.highSubtitle?.trim()
            if (high) scaleOptions.highSubtitle = high
            if (Object.keys(scaleOptions).length > 0) {
              questionPayload.scaleOptions = scaleOptions
            }
          }

          return questionPayload
        }),
        type: 'quest',
      }

      const response = await API.AdminAPI.quests.updateCustomForm(
        questId,
        payload,
      )

      // Use the fresh questions data from the API response
      const freshQuestions = response.data.questions || []

      // Process the fresh questions to match our component's format
      const processedQuestions = freshQuestions.map((q) => ({
        id: q._id || q.id,
        text: q.question || '',
        description: q.description || '',
        answerType: q.answerType || 'text',
        questionType: q.questionType || 'direct',
        dependentQuestionId: q.dependentQuestionId || '',
        condition: q.condition || [],
        isRequired: q.isRequired || false,
        originalOptions: Array.isArray(q.options) ? q.options : [],
        subQuestions: q.subQuestions || [],
        imageFileName: q.questionImageName || '',
        position: q.position || '',
        connectedTo: q.connectedTo || '',
        columnNameforReport: q.columnNameforReport || '',
        addressFields: reconstructAddressFields(q),
        isUploading: false,
        isDeleted: q.isDeleted || false,
        scaleOptions: q.scaleOptions,
      }))

      // Reset form with the fresh data from server
      resetForm({
        values: {
          questions: processedQuestions,
        },
      })

      Notification.showNotification('Question saved successfully!', 'success')

      // Call the parent onSave callback if provided
      if (onSave) {
        // Get the customFormId from the response
        const newCustomFormId =
          response?.data?.customFormId ||
          response?.data?._id ||
          response?.data?.id

        onSave(processedQuestions, newCustomFormId)
      }
    } catch (e) {
      if (e.code === 'custom_form_already_answered') {
        Notification.showNotification(e.message, 'danger')
      } else {
        Error.showError(e)
      }
    } finally {
      setIsSavingQuestion(false)
    }
  }

  // handleCancel is handled by the inline footer via onCancel

  // Delegate to utils to avoid duplication
  const parseLinksFromHtml = (htmlText) => parseLinksFromHtmlUtil(htmlText)

  // Selection helpers moved into LinkableTextEditor

  // Create initial values for Formik
  const initialValues = {
    questions: [
      {
        ...question,
        // Parse existing links from HTML or use existing links array
        links:
          Array.isArray(question.links) && question.links.length > 0
            ? question.links
            : parseLinksFromHtml(question.text),
        // Strip HTML from text for display in input field
        text:
          typeof question.text === 'string'
            ? question.text.replace(/<[^>]*>/g, '')
            : question.text,
        // Description uses the same link logic
        descriptionLinks:
          Array.isArray(question.descriptionLinks) &&
          question.descriptionLinks.length > 0
            ? question.descriptionLinks
            : parseLinksFromHtml(question.description || ''),
        description:
          typeof question.description === 'string'
            ? (question.description || '').replace(/<[^>]*>/g, '')
            : question.description || '',
        // Ensure dependentQuestion is included
        dependentQuestion: question.dependentQuestion || '',
        // Use originalOptions directly since we're removing the options array
        originalOptions: Array.isArray(question.options)
          ? question.options
          : Array.isArray(question.originalOptions)
          ? question.originalOptions
          : [],
      },
    ],
  }

  return (
    <Formik
      initialValues={initialValues}
      enableReinitialize
      onSubmit={handleFormSubmit}>
      {({ handleSubmit, setFieldValue, values, resetForm }) => {
        const currentQuestion = values.questions[0]

        // Preserve existing links when typing; do not auto-remove on text edits

        // Get the selected parent question with full data including originalOptions
        const selectedParentQuestion = allQuestionsForParent.find(
          (q) => (q.id || q._id) === currentQuestion.dependentQuestionId,
        )

        return (
          <form id='question-editor-form' onSubmit={handleSubmit}>
            <div className='experience-enrol-q-editor'>
              <div>
                {currentQuestion.questionType === 'dependent' && (
                  <span
                    className='d-inline-block mb-2 px-2 py-1'
                    style={{
                      backgroundColor: '#e3f2fd',
                      color: '#1976d2',
                      borderRadius: '4px',
                      fontSize: '12px',
                      fontWeight: 'bold',
                    }}>
                    DEPENDENT
                  </span>
                )}

                <LinkableTextEditor
                  label='Question'
                  placeholder='Add the question they see'
                  value={currentQuestion.text || ''}
                  hasError={Boolean(
                    validationErrors[actualIdx] &&
                      validationErrors[actualIdx].text,
                  )}
                  links={
                    Array.isArray(currentQuestion.links) &&
                    currentQuestion.links.length > 0
                      ? currentQuestion.links
                      : parseLinksFromHtml(currentQuestion.text || '')
                  }
                  onChangeText={(newText) => {
                    setFieldValue('questions[0].text', newText)
                    if (newText && newText.trim()) {
                      clearValidationError(actualIdx, null, 'text')
                    }
                  }}
                  onChangeLinks={(updated) =>
                    setFieldValue('questions[0].links', updated)
                  }
                  onModalStateChange={(val) => setShowLinkModal(val)}
                />
                {validationErrors[actualIdx] &&
                  validationErrors[actualIdx].text && (
                    <div
                      className='text-danger mt-1 small text-right'
                      role='alert'>
                      {validationErrors[actualIdx].text}
                    </div>
                  )}

                <div className='mt-12px'>
                  <LinkableTextEditor
                    label='Instruction'
                    placeholder='Add supporting instruction'
                    value={currentQuestion.description || ''}
                    links={
                      Array.isArray(currentQuestion.descriptionLinks) &&
                      currentQuestion.descriptionLinks.length > 0
                        ? currentQuestion.descriptionLinks
                        : parseLinksFromHtml(currentQuestion.description || '')
                    }
                    minHeight={92}
                    onChangeText={(newText) =>
                      setFieldValue('questions[0].description', newText)
                    }
                    onChangeLinks={(updated) =>
                      setFieldValue('questions[0].descriptionLinks', updated)
                    }
                    onModalStateChange={(val) => setShowLinkModal(val)}
                  />
                </div>

                <div className='experience-f-row mt-12px'>
                  <ExperienceSelect
                    label='Answer type'
                    value={currentQuestion.answerType}
                    onChange={(e) => {
                      const newType = e.target.value
                      setFieldValue('questions[0].answerType', newType)
                      if (
                        newType === 'singleSelectionDropdown' ||
                        newType === 'multipleSelectionDropdown'
                      ) {
                        setFieldValue('questions[0].originalOptions', [
                          { option: '' },
                        ])
                      } else if (newType === 'object') {
                        setFieldValue('questions[0].addressFields', {
                          address: true,
                          address2: true,
                          city: true,
                          state: true,
                          zip: true,
                          country: true,
                        })
                      } else if (newType === 'files') {
                        setFieldValue('questions[0].connectedTo', '')
                      } else if (newType === 'scale') {
                        setFieldValue('questions[0].scaleOptions', {
                          maxScaleLimit: 6,
                          lowSubtitle: '',
                          highSubtitle: '',
                        })
                      } else {
                        setFieldValue('questions[0].scaleOptions', undefined)
                      }
                      clearValidationError(actualIdx, null, 'connectedTo')
                    }}
                    options={QUESTION_TYPES}
                  />
                  <ExperienceInputField
                    name='questions[0].columnNameforReport'
                    label='Question text for export report'
                    placeholder='Optional column name'
                    capitalizeFirstLetter={true}
                    showErrorMessage={false}
                    displayFlex={false}
                  />
                </div>

                {currentQuestion.answerType === 'files' && (
                  <div className='mt-12px'>
                    <ExperienceSelect
                      label='File category'
                      value={currentQuestion.connectedTo}
                      placeholder='Select category'
                      onChange={(e) => {
                        setFieldValue(
                          'questions[0].connectedTo',
                          e.target.value,
                        )
                        if (e.target.value && e.target.value.trim() !== '') {
                          clearValidationError(actualIdx, null, 'connectedTo')
                        }
                      }}
                      isInvalid={Boolean(
                        validationErrors[actualIdx] &&
                          validationErrors[actualIdx].connectedTo,
                      )}
                      options={FILE_CATEGORIES}
                    />
                    {validationErrors[actualIdx] &&
                      validationErrors[actualIdx].connectedTo && (
                        <div
                          className='text-danger mt-1 small text-right'
                          role='alert'>
                          {validationErrors[actualIdx].connectedTo}
                        </div>
                      )}
                  </div>
                )}

                {(currentQuestion.answerType === 'singleSelectionDropdown' ||
                  currentQuestion.answerType ===
                    'multipleSelectionDropdown') && (
                  <div className='mt-12px'>
                    <label className='experience-f-label'>Options</label>
                    {(currentQuestion.originalOptions || []).map(
                      (opt, optIdx) => (
                        <div
                          className='d-flex align-items-center mb-2'
                          key={optIdx}>
                          <ExperienceInputField
                            name={`questions[0].originalOptions.${optIdx}.option`}
                            placeholder={`Option ${optIdx + 1}`}
                            capitalizeFirstLetter={true}
                            showErrorMessage={false}
                            displayFlex={false}
                            inputClasses={
                              validationErrors[actualIdx] &&
                              Array.isArray(
                                validationErrors[actualIdx].optionIndexes,
                              ) &&
                              validationErrors[
                                actualIdx
                              ].optionIndexes.includes(optIdx)
                                ? 'is-invalid'
                                : ''
                            }
                            onChange={(e) => {
                              setFieldValue(
                                `questions[0].originalOptions.${optIdx}.option`,
                                e.target.value,
                              )
                              if ((e.target.value || '').trim()) {
                                clearValidationError(actualIdx, optIdx)
                              }
                            }}
                          />
                          <button
                            type='button'
                            className='experience-linkbtn ml-2'
                            disabled={
                              (currentQuestion.originalOptions || []).length <=
                              1
                            }
                            onClick={() => {
                              const optionText = opt.option || opt.text || ''
                              const { canDelete, dependentQuestions } =
                                handleOptionDelete(
                                  optionText,
                                  optIdx,
                                  currentQuestion,
                                  allQuestionsForCondition,
                                )

                              if (!canDelete) {
                                setDeleteOptionModal({
                                  show: true,
                                  optionText: optionText,
                                  dependentQuestions: dependentQuestions,
                                })
                              } else {
                                const newOriginalOptions = (
                                  currentQuestion.originalOptions || []
                                ).filter((_, i) => i !== optIdx)
                                setFieldValue(
                                  'questions[0].originalOptions',
                                  newOriginalOptions,
                                )
                              }
                            }}>
                            Remove
                          </button>
                        </div>
                      ),
                    )}
                    {validationErrors[actualIdx] &&
                      validationErrors[actualIdx].options && (
                        <div
                          className='text-danger mt-1 mb-2 small text-right'
                          role='alert'>
                          {validationErrors[actualIdx].options}
                        </div>
                      )}
                    <button
                      type='button'
                      className='experience-linkbtn'
                      onClick={() => {
                        const newOriginalOptions = [
                          ...(currentQuestion.originalOptions || []),
                          { option: '' },
                        ]
                        setFieldValue(
                          'questions[0].originalOptions',
                          newOriginalOptions,
                        )
                      }}>
                      Add option
                    </button>
                  </div>
                )}

                {currentQuestion.answerType === 'object' && (
                  <div className='mt-12px'>
                    <label className='experience-f-label'>Address fields</label>
                    <p className='experience-f-hint mb-2'>
                      Address includes street, city, region, postal code and
                      country.
                    </p>
                    <div className='d-flex flex-column'>
                      {Object.entries({
                        address: 'Address',
                        address2: 'Address line 2',
                        city: 'City/Town',
                        state: 'State / Province / Region',
                        zip: 'Postal Code / Zip Code',
                        country: 'Country',
                      }).map(([key, label]) => (
                        <ExperienceCheckbox
                          key={key}
                          name={`questions[0].addressFields.${key}`}
                          label={label}
                          className='mb-2'
                          checked={!!currentQuestion.addressFields?.[key]}
                          onChange={(e) => {
                            setFieldValue(
                              `questions[0].addressFields.${key}`,
                              e.target.checked,
                            )
                          }}
                        />
                      ))}
                    </div>
                  </div>
                )}

                {currentQuestion.answerType === 'scale' && (
                  <div className='mt-12px'>
                    <label className='experience-f-label'>
                      Scale configuration
                    </label>
                    <p className='experience-f-hint mb-2'>
                      Participants pick a value from 1 to the maximum. Low and
                      high labels are optional.
                    </p>
                    <div className='experience-f-row'>
                      <ExperienceInputField
                        name='questions[0].scaleOptions.lowSubtitle'
                        label='Low subtitle'
                        placeholder='e.g., Strongly disagree'
                        capitalizeFirstLetter={true}
                        showErrorMessage={false}
                        displayFlex={false}
                      />
                      <ExperienceInputField
                        name='questions[0].scaleOptions.highSubtitle'
                        label='High subtitle'
                        placeholder='e.g., Strongly agree'
                        capitalizeFirstLetter={true}
                        showErrorMessage={false}
                        displayFlex={false}
                      />
                    </div>
                    <div className='mt-12px' style={{ maxWidth: 200 }}>
                      <ExperienceSelect
                        label='Maximum scale limit'
                        value={currentQuestion.scaleOptions?.maxScaleLimit || 6}
                        onChange={(e) => {
                          setFieldValue(
                            'questions[0].scaleOptions.maxScaleLimit',
                            parseInt(e.target.value, 10),
                          )
                        }}
                        options={[5, 6, 7, 8, 9, 10].map((limit) => ({
                          value: limit,
                          label: String(limit),
                        }))}
                      />
                    </div>
                  </div>
                )}

                <div className='experience-f-row mt-12px'>
                  <ExperienceSelect
                    label='Question type'
                    value={currentQuestion.questionType || 'direct'}
                    disabled={
                      getParentQuestionsWithDisabledState(
                        currentQuestion,
                      ).filter((q) => !q.isDisabled).length === 0
                    }
                    onChange={(e) => {
                      const newQuestionType = e.target.value
                      setFieldValue(
                        'questions[0].questionType',
                        newQuestionType,
                      )
                      if (newQuestionType === 'direct') {
                        setFieldValue('questions[0].dependentQuestionId', '')
                        setFieldValue('questions[0].condition', [])
                      }
                    }}
                    options={[
                      { value: 'direct', label: 'Direct' },
                      { value: 'dependent', label: 'Dependent' },
                    ]}
                  />
                  {currentQuestion.questionType === 'dependent' ? (
                    <div>
                      {(() => {
                        const parentQuestionsWithState =
                          getParentQuestionsWithDisabledState(currentQuestion)

                        return (
                          <>
                            <ExperienceSelect
                              label='Depends on'
                              value={currentQuestion.dependentQuestionId || ''}
                              disabled={
                                parentQuestionsWithState.filter(
                                  (q) => !q.isDisabled,
                                ).length === 0
                              }
                              placeholder={
                                parentQuestionsWithState.filter(
                                  (q) => !q.isDisabled,
                                ).length === 0
                                  ? 'No parent questions available'
                                  : 'Select parent question'
                              }
                              onChange={(e) => {
                                const selectedQuestionId = e.target.value
                                setFieldValue(
                                  'questions[0].dependentQuestionId',
                                  selectedQuestionId,
                                )

                                if (selectedQuestionId) {
                                  const selectedParent =
                                    parentQuestionsWithState.find(
                                      (q) =>
                                        (q.id || q._id) === selectedQuestionId,
                                    )
                                  if (selectedParent) {
                                    setFieldValue(
                                      'questions[0].dependentQuestion',
                                      selectedParent.text ||
                                        selectedParent.displayText ||
                                        '',
                                    )
                                  }
                                } else {
                                  setFieldValue(
                                    'questions[0].dependentQuestion',
                                    '',
                                  )
                                }

                                setFieldValue('questions[0].condition', [])
                                clearValidationError(
                                  actualIdx,
                                  null,
                                  'dependentQuestionId',
                                )
                              }}
                              isInvalid={Boolean(
                                validationErrors[actualIdx] &&
                                  validationErrors[actualIdx]
                                    .dependentQuestionId,
                              )}
                              isOptionDisabled={(opt) => !!opt.disabled}
                              options={parentQuestionsWithState.map((q) => ({
                                value: q.id || q._id,
                                label: q.displayText,
                                disabled: q.isDisabled,
                              }))}
                            />
                            {validationErrors[actualIdx] &&
                              validationErrors[actualIdx]
                                .dependentQuestionId && (
                                <div
                                  className='text-danger mt-1 small text-right'
                                  role='alert'>
                                  {
                                    validationErrors[actualIdx]
                                      .dependentQuestionId
                                  }
                                </div>
                              )}
                          </>
                        )
                      })()}
                    </div>
                  ) : (
                    <div />
                  )}
                </div>

                {currentQuestion.questionType === 'dependent' &&
                  currentQuestion.dependentQuestionId && (
                    <div className='mt-12px'>
                      {(() => {
                        const actualParentQuestion = selectedParentQuestion

                        if (
                          !actualParentQuestion?.originalOptions ||
                          actualParentQuestion.originalOptions.length === 0 ||
                          !(actualParentQuestion.originalOptions || []).some(
                            (opt) =>
                              (opt?.option || opt?.text || '').trim().length >
                              0,
                          )
                        ) {
                          return (
                            <div className='experience-f'>
                              <label className='experience-f-label'>
                                Condition
                              </label>
                              <p className='experience-f-hint mb-0 text-dark'>
                                This parent has no options. Add options to the
                                parent question, then choose a condition here.
                              </p>
                              {validationErrors[actualIdx] &&
                                validationErrors[actualIdx].condition && (
                                  <div
                                    className='text-danger mt-1 small text-right'
                                    role='alert'>
                                    {validationErrors[actualIdx].condition}
                                  </div>
                                )}
                            </div>
                          )
                        }

                        const usedConditions = allQuestionsForCondition
                          .filter(
                            (q, idx) =>
                              q.questionType === 'dependent' &&
                              q.dependentQuestionId ===
                                currentQuestion.dependentQuestionId &&
                              q.condition &&
                              q.condition.length > 0 &&
                              idx !== actualIdx,
                          )
                          .map((q) => {
                            const conditionId = q.condition[0]
                            return getOptionValueById(
                              actualParentQuestion,
                              conditionId,
                            )
                          })
                          .filter(Boolean)

                        const convertedCondition = convertConditionToIds(
                          currentQuestion.condition,
                          actualParentQuestion,
                        )
                        if (
                          JSON.stringify(convertedCondition) !==
                          JSON.stringify(currentQuestion.condition)
                        ) {
                          setFieldValue(
                            'questions[0].condition',
                            convertedCondition,
                          )
                        }

                        const isSingleSelectionParent =
                          actualParentQuestion.answerType ===
                          'singleSelectionDropdown'
                        const isMultipleSelectionParent =
                          actualParentQuestion.answerType ===
                          'multipleSelectionDropdown'

                        if (isMultipleSelectionParent) {
                          const selectOptions = (
                            actualParentQuestion.originalOptions || []
                          ).map((option) => {
                            const optionValue =
                              typeof option === 'object' && option !== null
                                ? option.option
                                : option
                            const optionId =
                              typeof option === 'object' && option !== null
                                ? option.id || option._id
                                : null
                            return {
                              value: optionId || optionValue,
                              label: optionValue,
                              optionValue: optionValue,
                            }
                          })

                          const selectedValues = (
                            currentQuestion.condition || []
                          ).map((conditionId) => {
                            const optionValue =
                              getOptionValueById(
                                actualParentQuestion,
                                conditionId,
                              ) || conditionId
                            return {
                              value: conditionId,
                              label: optionValue,
                            }
                          })

                          return (
                            <div className='experience-f'>
                              <label className='experience-f-label'>
                                Condition
                              </label>
                              <Select
                                isMulti
                                options={selectOptions}
                                value={selectedValues}
                                onChange={(selectedOptions) => {
                                  const selectedConditions = selectedOptions
                                    ? selectedOptions.map(
                                        (option) => option.value,
                                      )
                                    : []
                                  setFieldValue(
                                    'questions[0].condition',
                                    selectedConditions,
                                  )
                                  clearValidationError(
                                    actualIdx,
                                    null,
                                    'condition',
                                  )
                                }}
                                placeholder='Select conditions...'
                                classNamePrefix='experience-select'
                                className={
                                  validationErrors[actualIdx] &&
                                  validationErrors[actualIdx].condition
                                    ? 'experience-select-control is-invalid'
                                    : 'experience-select-control'
                                }
                                isInvalid={Boolean(
                                  validationErrors[actualIdx] &&
                                    validationErrors[actualIdx].condition,
                                )}
                                menuPortalTarget={
                                  typeof document !== 'undefined'
                                    ? document.body
                                    : null
                                }
                                styles={{
                                  control: (provided, state) => {
                                    const hasError = Boolean(
                                      validationErrors[actualIdx] &&
                                        validationErrors[actualIdx].condition,
                                    )
                                    const borderColor = hasError
                                      ? 'var(--stop, #c73939)'
                                      : state.isFocused
                                      ? 'var(--gold, #dfae3f)'
                                      : 'var(--line2, #cfccc4)'
                                    return {
                                      ...provided,
                                      minHeight: 38,
                                      borderRadius: 7,
                                      borderColor,
                                      borderWidth: 1,
                                      boxShadow: state.isFocused
                                        ? hasError
                                          ? '0 0 0 2px rgba(199, 57, 57, 0.2)'
                                          : '0 0 0 2px rgba(223, 174, 63, 0.25)'
                                        : 'none',
                                      fontSize: 13.5,
                                      '&:hover': {
                                        borderColor,
                                      },
                                    }
                                  },
                                  menuPortal: (provided) => ({
                                    ...provided,
                                    zIndex: 1060,
                                  }),
                                }}
                              />
                              {validationErrors[actualIdx] &&
                                validationErrors[actualIdx].condition && (
                                  <div
                                    className='text-danger mt-1 small text-right'
                                    role='alert'>
                                    {validationErrors[actualIdx].condition}
                                  </div>
                                )}
                            </div>
                          )
                        }

                        if (isSingleSelectionParent) {
                          const conditionValue = (() => {
                            const conditionId = currentQuestion.condition?.[0]
                            if (!conditionId) return ''

                            const optionValue = getOptionValueById(
                              actualParentQuestion,
                              conditionId,
                            )
                            if (optionValue) {
                              const option =
                                actualParentQuestion.originalOptions.find(
                                  (opt) => {
                                    const optValue =
                                      typeof opt === 'object' && opt !== null
                                        ? opt.option
                                        : opt
                                    return optValue === optionValue
                                  },
                                )
                              const optionId =
                                typeof option === 'object' && option !== null
                                  ? option.id || option._id
                                  : null
                              return optionId || conditionId
                            }
                            return conditionId
                          })()

                          const conditionOptions = (
                            actualParentQuestion.originalOptions || []
                          ).map((option, optIdx) => {
                            const optionValue =
                              typeof option === 'object' && option !== null
                                ? option.option
                                : option
                            const optionId =
                              typeof option === 'object' && option !== null
                                ? option.id || option._id
                                : null
                            const isUsed = usedConditions.includes(optionValue)
                            const isCurrentSelection =
                              currentQuestion.condition &&
                              currentQuestion.condition[0] ===
                                (optionId || optionValue)
                            const isDisabled = isUsed && !isCurrentSelection

                            return {
                              value: optionId || optionValue,
                              label: `${optionValue}${
                                isDisabled ? ' (Used)' : ''
                              }`,
                              disabled: isDisabled,
                              key: optIdx,
                            }
                          })

                          return (
                            <>
                              <ExperienceSelect
                                label='Condition'
                                value={conditionValue}
                                placeholder='Select condition'
                                onChange={(e) => {
                                  const selectedCondition = e.target.value
                                  setFieldValue(
                                    'questions[0].condition',
                                    selectedCondition
                                      ? [selectedCondition]
                                      : [],
                                  )
                                  clearValidationError(
                                    actualIdx,
                                    null,
                                    'condition',
                                  )
                                }}
                                isInvalid={Boolean(
                                  validationErrors[actualIdx] &&
                                    validationErrors[actualIdx].condition,
                                )}
                                isOptionDisabled={(opt) => !!opt.disabled}
                                options={conditionOptions}
                              />
                              {validationErrors[actualIdx] &&
                                validationErrors[actualIdx].condition && (
                                  <div
                                    className='text-danger mt-1 small text-right'
                                    role='alert'>
                                    {validationErrors[actualIdx].condition}
                                  </div>
                                )}
                            </>
                          )
                        }

                        return (
                          <div className='experience-f'>
                            <label className='experience-f-label'>
                              Condition
                            </label>
                            <p className='experience-f-hint mb-0 text-dark'>
                              Pick a parent dropdown with options to set a
                              condition.
                            </p>
                            {validationErrors[actualIdx] &&
                              validationErrors[actualIdx].condition && (
                                <div
                                  className='text-danger mt-1 small text-right'
                                  role='alert'>
                                  {validationErrors[actualIdx].condition}
                                </div>
                              )}
                          </div>
                        )
                      })()}
                    </div>
                  )}

                <div className='mt-12px'>
                  <ExperienceCheckbox
                    name='questions[0].isRequired'
                    label='Required question'
                    checked={!!currentQuestion.isRequired}
                    onChange={(e) => {
                      setFieldValue('questions[0].isRequired', e.target.checked)
                    }}
                  />
                </div>
              </div>

              <div className='experience-f'>
                <label className='experience-f-label'>Question image</label>
                <div className='experience-img-slot' data-ratio='2:1'>
                  <div
                    className={classNames('experience-img-frame', {
                      'is-empty':
                        !currentQuestion.imageFileName &&
                        !currentQuestion?.isUploading,
                      'is-dragging': isDraggingImage,
                    })}
                    onClick={() => {
                      if (!currentQuestion?.isUploading) {
                        handleFileClick(actualIdx)
                      }
                    }}
                    onDragEnter={handleDragEnter}
                    onDragOver={handleDragOver}
                    onDragLeave={handleDragLeave}
                    onDrop={handleDrop}
                    role='button'
                    tabIndex={0}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault()
                        if (!currentQuestion?.isUploading) {
                          handleFileClick(actualIdx)
                        }
                      }
                    }}>
                    {currentQuestion?.isUploading ? (
                      <span
                        className='experience-img-hover'
                        style={{ opacity: 1 }}>
                        {removingImageIdx === actualIdx
                          ? 'Removing…'
                          : 'Uploading…'}
                      </span>
                    ) : (
                      <span className='experience-img-hover'>
                        {isDraggingImage
                          ? 'Drop image to upload'
                          : currentQuestion.imageFileName
                          ? 'Click to replace'
                          : 'Upload image'}
                      </span>
                    )}

                    {currentQuestion.imageFileName ? (
                      currentQuestion.imageFileName.startsWith('data:') ? (
                        <img
                          src={currentQuestion.imageFileName}
                          alt='question'
                        />
                      ) : (
                        <Imgix
                          imgixParams={{
                            fit: 'crop',
                            crop: 'center',
                            ar: '2:1',
                          }}
                          src={`${process.env.REACT_APP_IMG_SOURCE}/forms/images/${currentQuestion.imageFileName}`}
                          attributeConfig={{
                            src: 'data-src',
                            srcSet: 'data-srcset',
                            sizes: 'data-sizes',
                          }}
                          htmlAttributes={{
                            src: `${process.env.REACT_APP_IMG_SOURCE}/forms/images/${currentQuestion.imageFileName}?ar=2:1&fit=crop&crop=center&fill=blur&blur=300&px=16&auto=format`,
                            style: {
                              width: '100%',
                              height: '100%',
                              objectFit: 'cover',
                            },
                          }}
                          alt='question'
                          className={classNames('lazyload', {
                            itenaryImage: currentQuestion.isUploading,
                          })}
                        />
                      )
                    ) : !currentQuestion?.isUploading ? (
                      <>
                        <b>Upload image</b>
                        <small>JPG, JPEG or PNG · cropped to 2:1</small>
                      </>
                    ) : null}

                    {currentQuestion.imageFileName &&
                      !currentQuestion?.isUploading && (
                        <button
                          type='button'
                          className='experience-img-remove'
                          aria-label='Remove image'
                          onClick={(e) => {
                            e.stopPropagation()
                            e.preventDefault()
                            handleRemoveQuestionImage(
                              actualIdx,
                              currentQuestion,
                              setFieldValue,
                            )
                          }}>
                          ×
                        </button>
                      )}

                    <Form.Control
                      type='file'
                      size='lg'
                      placeholder='Select File'
                      accept='image/jpg, image/jpeg, image/png'
                      onChange={async (e) => {
                        const target = e.target
                        const files = e.target.files
                        if (files && files?.length > 0) {
                          openCropModal(files[0], () => {
                            if (target) target.value = ''
                          })
                        }
                      }}
                      style={{ display: 'none' }}
                      name={`questions[${actualIdx}].imageFileName`}
                      sm='10'
                      ref={imageRefs?.current?.[actualIdx] || localImageRef}
                    />
                  </div>
                </div>

                {currentQuestion.imageFileName && (
                  <div className='mt-12px'>
                    <ExperienceSelect
                      label='Position of image'
                      value={currentQuestion.position}
                      onChange={(e) => {
                        setFieldValue('questions[0].position', e.target.value)
                      }}
                      options={[
                        { value: 'left', label: 'Left' },
                        { value: 'right', label: 'Right' },
                      ]}
                    />
                  </div>
                )}
              </div>
            </div>

            <ImageCropModal
              show={showImageCropModal}
              onHide={handleCropModalClose}
              file={selectedImageFile}
              onConfirm={async (croppedImageFile) => {
                if (!croppedImageFile) return
                setShowImageCropModal(false)
                setFieldValue(`questions[0].isUploading`, true)
                if (onImageUploadStateChange) onImageUploadStateChange(true)
                const reader = new FileReader()
                reader.onloadend = async () => {
                  setFieldValue(`questions[0].imageFileName`, reader.result)
                  try {
                    const fileName = await uploadQuestionImage(croppedImageFile)
                    setFieldValue(`questions[0].imageFileName`, fileName)
                    setFieldValue(`questions[0].isUploading`, false)
                    if (onImageUploadStateChange)
                      onImageUploadStateChange(false)
                  } catch (e) {
                    setFieldValue(`questions[0].isUploading`, false)
                    if (onImageUploadStateChange)
                      onImageUploadStateChange(false)
                    Notification.showNotification(
                      'Image upload failed',
                      'danger',
                    )
                  } finally {
                    setSelectedImageFile(null)
                    resetImageInput()
                    setIsDraggingImage(false)
                  }
                }
                reader.readAsDataURL(croppedImageFile)
              }}
              isUploading={currentQuestion?.isUploading}
              aspect={2 / 1}
              ratioLabel='2:1'
              title='Crop Image'
            />

            <DeleteOptionModal
              show={deleteOptionModal.show}
              onHide={() =>
                setDeleteOptionModal({
                  show: false,
                  optionText: '',
                  dependentQuestions: [],
                })
              }
              optionText={deleteOptionModal.optionText}
              dependentQuestions={deleteOptionModal.dependentQuestions}
            />

            {(onSave || onCancel) && (
              <div className='experience-form-actions'>
                <ExperienceButton
                  type='submit'
                  variant='primary'
                  size='sm'
                  disabled={
                    isSaving || isSavingQuestion || currentQuestion?.isUploading
                  }>
                  {isSaving || isSavingQuestion ? 'Saving…' : 'Save question'}
                </ExperienceButton>
                <ExperienceButton
                  type='button'
                  variant='outline'
                  size='sm'
                  onClick={onCancel}
                  disabled={
                    isSaving || isSavingQuestion || currentQuestion?.isUploading
                  }>
                  Cancel
                </ExperienceButton>
              </div>
            )}
          </form>
        )
      }}
    </Formik>
  )
}

export default QuestionEditor
